import { useMutation, useQuery } from '@apollo/client';
import { ColumnsType } from 'antd/es/table';
import { useNavigate, useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import { BCP_HUB } from '../../../../../constants';
import { useUserContext } from '../../../../../context/PermissionsContext';

import { Button, Footer, TitleBar } from '../../../../../components';
import TableV3, {
  ModifyVariablesArgs,
} from '../../../../../components/generic/Table/TableV3';
import {
  DocumentLinks,
  OwnersInformation,
  SummaryContent,
} from '../../components';

import {
  CREATE_PEOPLE,
  DELETE_PEOPLE,
  FIND_ALL_PEOPLE_BY_STRATEGY_PEOPLE,
  FIND_STRATEGY_PEOPLE_BY_STRATEGY,
  GET_SECTION_OWNER,
  UPDATE_PEOPLE,
  UPDATE_SECTION_OWNER,
  UPDATE_STRATEGY_PEOPLE,
} from '../../../queries';
import { GET_DOCUMENT_LINKS_BY_PEOPLE } from '../../../queries/documentLink';
import { STRATEGY_MODELS, STRATEGY_TYPES } from '../constants';
import { formFields } from './formFields';
import { tableSortingMethods } from '../../../../../utils/tableSortingMethods';
import { baseColumns } from './exportColumns';
import TitleUpdater from '../../../../../components/common/TitleUpdater';

type StrategyFacilityProps = {
  strategyType?: string;
};

const PeoplePA = ({ strategyType = 'processArea' }: StrategyFacilityProps) => {
  const { locationId, processAreaId, strategyId } = useParams();
  const [searchParams] = useSearchParams();

  const locationName = searchParams.get('locationName');
  const strategyLength = searchParams.get('strategyLength');

  let strategyName;

  if (strategyType === 'processArea') {
    strategyName = searchParams.get('name');
  }

  const subtitle =
    strategyType === 'processArea'
      ? `${locationName} // ${strategyName} // ${strategyLength} Month Strategy`
      : `${locationName} // ${strategyLength} Month Strategy`;

  const backPath =
    strategyType === 'processArea'
      ? `/process-areas-and-strategies/${locationId}/${processAreaId}`
      : `/strategy-summary/${locationId}/${strategyId}?locationName=${locationName}&strategyLength=${strategyLength}`;

  const OPERATION_NAMES = {
    SECTION_OWNER: 'GetSectionOwnerByStrategy',
    PEOPLE: 'FindAllPeopleByStrategyPeople',
    STRATEGY_PEOPLE: 'FindStrategyPeopleByStrategy',
    DOCUMENTS: 'FindAllByStrategyPeople',
  };

  const {
    data: strategyPeople,
    loading: strategyPeopleLoading,
    error: strategyPeopleError,
  } = useQuery(FIND_STRATEGY_PEOPLE_BY_STRATEGY, {
    variables: {
      uuid: strategyId,
    },
    skip: !strategyId,
  });

  const strategyPeopleData = strategyPeople?.findStrategyPeopleByStrategy || {};

  const { data: strategyOwner, error: strategyOwnerError } = useQuery(
    GET_SECTION_OWNER,
    {
      variables: {
        uuid: strategyId,
        strategyType: STRATEGY_TYPES.PEOPLE,
      },
      skip: !strategyId,
    },
  );

  const sectionOwner = strategyOwner?.findSectionOwnerByStrategy;

  const {
    data: people,
    loading: peopleLoading,
    error: peopleError,
  } = useQuery(FIND_ALL_PEOPLE_BY_STRATEGY_PEOPLE, {
    variables: {
      uuid: strategyPeopleData?.uuid,
    },
    skip: !strategyPeopleData?.uuid,
  });

  const peopleData = people?.findAllPeopleByStrategyPeople;

  const { data: documents, error: documentsError } = useQuery(
    GET_DOCUMENT_LINKS_BY_PEOPLE,
    {
      variables: {
        uuid: strategyPeopleData?.uuid,
      },
      skip: !strategyPeopleData?.uuid,
    },
  );

  const documentsData = documents?.findAllByStrategyPeople;

  const [updateSectionOwner] = useMutation(UPDATE_SECTION_OWNER, {
    refetchQueries: [OPERATION_NAMES.SECTION_OWNER],
  });

  const [createPeople] = useMutation(CREATE_PEOPLE, {
    refetchQueries: [OPERATION_NAMES.PEOPLE],
  });
  const [updatePeople] = useMutation(UPDATE_PEOPLE, {
    refetchQueries: [OPERATION_NAMES.PEOPLE],
  });
  const [deletePeople] = useMutation(DELETE_PEOPLE, {
    refetchQueries: [OPERATION_NAMES.PEOPLE],
  });

  const [updateStrategyPeople] = useMutation(UPDATE_STRATEGY_PEOPLE, {
    refetchQueries: [OPERATION_NAMES.STRATEGY_PEOPLE],
  });

  const customColumns: ColumnsType<any> = [
    {
      key: 'functionName',
      title: 'Function/Type',
      width: 150,
      align: 'left',
      sorter: (a, b) =>
        tableSortingMethods.string(a.functionName, b.functionName),
      render: ({ functionName }) => (
        <span className="custom-table--column custom-table--column-primary">
          {functionName}
        </span>
      ),
    },
    {
      key: 'employeesRequired',
      title: 'Employees Required',
      width: 100,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(a.employeesRequired, b.employeesRequired),
      render: ({ employeesRequired }) =>
        employeesRequired >= 0 ? (
          <div className="custom-table--column">{employeesRequired}</div>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      key: 'numberToHire',
      title: 'Number to Hire',
      width: 100,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(a.numberToHire, b.numberToHire),
      render: ({ numberToHire }) =>
        numberToHire >= 0 ? (
          <span className="custom-table--column">{numberToHire}</span>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      key: 'hiringLeadTime',
      title: () => (
        <div>
          Hiring Lead Time <br />
          (months)
        </div>
      ),
      width: 100,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(a.hiringLeadTime, b.hiringLeadTime),
      render: ({ hiringLeadTime }) =>
        hiringLeadTime ? (
          <span className="custom-table--column">{hiringLeadTime}</span>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      key: 'qualificationRequired',
      title: 'Qualifications/Requirements',
      width: 150,
      align: 'left',
      sorter: (a, b) =>
        tableSortingMethods.string(
          a.qualificationRequired,
          b.qualificationRequired,
        ),
      render: ({ qualificationRequired }) =>
        qualificationRequired ? (
          <span className="custom-table--column">{qualificationRequired}</span>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
  ];

  const modifyVariables = ({ values, type }: ModifyVariablesArgs) => {
    const commonValues = {
      numberToHire: Number(values.numberToHire),
      employeesRequired: Number(values.employeesRequired),
      hiringLeadTime: String(values.hiringLeadTime),
    };

    if (type === 'create') {
      return {
        ...values,
        ...commonValues,
        strategyPeople: {
          uuid: strategyPeopleData?.uuid,
        },
      };
    }

    return {
      ...values,
      ...commonValues,
    };
  };

  return (
    <>
      <TitleUpdater title={`People`} />
      <TitleBar title="BCP Hub" subtitle={subtitle} />
      <section className="flex flex-col gap-4">
        <OwnersInformation
          title="People Section Owner"
          data={sectionOwner}
          updateGQL={updateSectionOwner}
          error={strategyOwnerError}
        />
        <TableV3
          title="People Requirements"
          columns={customColumns}
          data={peopleData}
          loading={peopleLoading}
          error={peopleError}
          editable
          formFields={formFields}
          createGQL={createPeople}
          updateGQL={updatePeople}
          deleteGQL={deletePeople}
          modifyVariables={modifyVariables}
          exportExcelColumns={baseColumns}
        />
        <section className="w-full grid xl:grid-cols-2 gap-4">
          <SummaryContent
            title="People Commentary"
            data={strategyPeopleData}
            updateGQL={updateStrategyPeople}
            loading={strategyPeopleLoading}
            error={strategyPeopleError}
          />
          <DocumentLinks
            data={documentsData}
            error={documentsError}
            parentUuid={strategyPeopleData?.uuid}
            queryToUpdate={OPERATION_NAMES.DOCUMENTS}
            model={STRATEGY_MODELS.PEOPLE}
          />
        </section>
        <Footer backPath={backPath} />
      </section>
    </>
  );
};

export default PeoplePA;
